
import { useCallback } from 'react';

type AnimationVariants = {
  [key: string]: any;
};

export type AnimationOptions = {
  duration?: number;
  delay?: number;
  easing?: string | number[];
};

/**
 * A hook to generate consistent animation variants for Framer Motion
 * 
 * @param options Animation configuration options
 * @returns Animation variant objects
 */
export const useAnimationVariants = (options: AnimationOptions = {}) => {
  const { 
    duration = 0.5, 
    delay = 0, 
    easing = [0.19, 1, 0.22, 1] 
  } = options;

  /**
   * Fade animation variants
   */
  const fade = useCallback((): AnimationVariants => ({
    initial: { opacity: 0 },
    animate: { 
      opacity: 1,
      transition: { 
        duration,
        delay,
        ease: easing
      }
    },
    exit: { 
      opacity: 0,
      transition: { 
        duration: duration * 0.75,
      }
    }
  }), [duration, delay, easing]);

  /**
   * Scale animation variants
   */
  const scale = useCallback((): AnimationVariants => ({
    initial: { scale: 0.95, opacity: 0 },
    animate: {
      scale: 1,
      opacity: 1,
      transition: {
        duration,
        delay,
        ease: easing
      }
    },
    exit: {
      scale: 0.95,
      opacity: 0,
      transition: {
        duration: duration * 0.75
      }
    }
  }), [duration, delay, easing]);

  /**
   * Slide up animation variants
   */
  const slideUp = useCallback((): AnimationVariants => ({
    initial: { y: 20, opacity: 0 },
    animate: {
      y: 0,
      opacity: 1,
      transition: {
        duration,
        delay,
        ease: easing
      }
    },
    exit: {
      y: 20,
      opacity: 0,
      transition: {
        duration: duration * 0.75
      }
    }
  }), [duration, delay, easing]);

  /**
   * Slide down animation variants
   */
  const slideDown = useCallback((): AnimationVariants => ({
    initial: { y: -20, opacity: 0 },
    animate: {
      y: 0,
      opacity: 1,
      transition: {
        duration,
        delay,
        ease: easing
      }
    },
    exit: {
      y: -20,
      opacity: 0,
      transition: {
        duration: duration * 0.75
      }
    }
  }), [duration, delay, easing]);

  /**
   * Slide in from right animation variants
   */
  const slideRight = useCallback((): AnimationVariants => ({
    initial: { x: -20, opacity: 0 },
    animate: {
      x: 0,
      opacity: 1,
      transition: {
        duration,
        delay,
        ease: easing
      }
    },
    exit: {
      x: -20,
      opacity: 0,
      transition: {
        duration: duration * 0.75
      }
    }
  }), [duration, delay, easing]);

  /**
   * Slide in from left animation variants
   */
  const slideLeft = useCallback((): AnimationVariants => ({
    initial: { x: 20, opacity: 0 },
    animate: {
      x: 0,
      opacity: 1,
      transition: {
        duration,
        delay,
        ease: easing
      }
    },
    exit: {
      x: 20,
      opacity: 0,
      transition: {
        duration: duration * 0.75
      }
    }
  }), [duration, delay, easing]);

  /**
   * Combined fade and scale animation variants
   */
  const fadeScale = useCallback((): AnimationVariants => ({
    initial: { opacity: 0, scale: 0.95 },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration,
        delay,
        ease: easing
      }
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: duration * 0.75
      }
    }
  }), [duration, delay, easing]);

  return {
    fade,
    scale,
    slideUp,
    slideDown,
    slideLeft,
    slideRight,
    fadeScale
  };
};

export default useAnimationVariants;
